import React, { useState, useEffect } from 'react';
import { httpClient } from '../services/httpClient';
import { ENDPOINTS } from '../config/api.config';

const AdminSettings = () => {
  const [rules, setRules] = useState({ service: [], vendor: [] });
  const [loading, setLoading] = useState(false);
  const [selectedType, setSelectedType] = useState('service');
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchRules();
  }, []);

  const fetchRules = async () => {
    try {
      setLoading(true);
      const response = await httpClient.get(`${ENDPOINTS.ADMIN_API}/verification-rules`);
      const data = response?.data || { service: [], vendor: [] };
      setRules(data);
    } catch (error) {
      console.error('Error fetching rules:', error);
      setMessage('Failed to load verification rules');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRule = async (ruleId, changes) => {
    try {
      await httpClient.put(`${ENDPOINTS.ADMIN_API}/verification-rules/${ruleId}`, changes);
      setMessage('✅ Rule updated successfully');
      setTimeout(() => {
        fetchRules();
        setMessage('');
      }, 1500);
    } catch (error) {
      setMessage('❌ Failed to update rule');
      console.error(error);
    }
  };

  const currentRules = rules[selectedType] || [];

  // Group rules by field
  const groupedRules = currentRules.reduce((acc, rule) => {
    if (!acc[rule.field]) {
      acc[rule.field] = [];
    }
    acc[rule.field].push(rule);
    return acc;
  }, {});

  return (
    <div className="admin-section">
      <div className="section-header">
        <h2>⚙️ Verification Settings</h2>
      </div>

      {message && (
        <div style={{ 
          padding: '12px', 
          marginBottom: '16px', 
          background: 'rgba(16, 185, 129, 0.1)',
          border: '1px solid rgba(16, 185, 129, 0.3)',
          borderRadius: '6px',
          color: '#10b981'
        }}>
          {message}
        </div>
      )}

      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
        {['service', 'vendor'].map(type => (
          <button
            key={type}
            onClick={() => setSelectedType(type)}
            style={{
              padding: '10px 16px',
              background: selectedType === type ? 'var(--admin-primary)' : 'var(--admin-sidebar)',
              color: 'var(--admin-text)',
              border: selectedType === type ? 'none' : '1px solid var(--admin-border)',
              borderRadius: '6px',
              cursor: 'pointer',
              textTransform: 'capitalize',
              fontWeight: '600',
              fontSize: '0.9rem'
            }}
          >
            {type === 'service' ? '🔧 Service Rules' : '👥 Vendor Rules'}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '20px' }}>⏳ Loading verification rules...</div>
      ) : Object.keys(groupedRules).length === 0 ? (
        <div style={{ textAlign: 'center', padding: '20px', color: 'var(--admin-text-secondary)' }}>
          No verification rules found for {selectedType}
        </div>
      ) : (
        <div className="admin-table">
          <table style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Field</th>
                <th style={{width: '120px'}}>Validation Type</th>
                <th style={{width: '80px'}}>Value</th>
                <th>Description</th>
                <th style={{width: '70px'}}>Active</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(groupedRules).map(([fieldName, fieldRules]) => (
                <React.Fragment key={fieldName}>
                  {fieldRules.map((rule, idx) => (
                    <tr key={`${fieldName}-${idx}`}>
                      <td>
                        <strong style={{ fontSize: '0.9rem' }}>{fieldName}</strong>
                      </td>
                      <td>
                        <span style={{ 
                          background: 'rgba(59, 130, 246, 0.15)',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '0.8rem',
                          whiteSpace: 'nowrap',
                          display: 'inline-block'
                        }}>
                          {rule.rule_type === 'min_words' && '📝 Min Words'} 
                          {rule.rule_type === 'min_count' && '📊 Min Count'}
                          {rule.rule_type === 'exists' && '✓ Required'}
                          {rule.rule_type === 'regex' && '🔍 Pattern'}
                        </span>
                      </td>
                      <td>
                        <input
                          type="text"
                          defaultValue={rule.value}
                          onBlur={(e) => {
                            if (e.target.value !== rule.value) {
                              handleUpdateRule(rule.id, { value: e.target.value });
                            }
                          }}
                          style={{
                            padding: '6px 8px',
                            background: 'var(--admin-sidebar)',
                            border: '1px solid var(--admin-border)',
                            borderRadius: '4px',
                            color: 'var(--admin-text)',
                            width: '70px',
                            fontSize: '0.85rem',
                            textAlign: 'center'
                          }}
                        />
                      </td>
                      <td style={{ fontSize: '0.8rem', color: 'var(--admin-text-secondary)' }}>
                        {rule.rule_type === 'min_words' && `Min ${rule.value} words`}
                        {rule.rule_type === 'min_count' && `Min ${rule.value} items`}
                        {rule.rule_type === 'exists' && 'Required field'}
                      </td>
                      <td>
                        <button
                          onClick={() => handleUpdateRule(rule.id, { is_active: !rule.is_active })}
                          style={{
                            padding: '4px 10px',
                            background: rule.is_active ? '#10b981' : '#6b7280',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {rule.is_active ? '✓' : '✕'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ marginTop: '20px', padding: '12px', background: 'rgba(59, 130, 246, 0.08)', border: '1px solid rgba(59, 130, 246, 0.2)', borderRadius: '6px' }}>
        <h3 style={{ marginTop: 0, marginBottom: '8px', fontSize: '0.95rem' }}>📋 Active {selectedType === 'service' ? 'Service' : 'Vendor'} Requirements:</h3>
        <ul style={{ marginBottom: 0, paddingLeft: '18px', fontSize: '0.85rem' }}>
          {currentRules
            .filter(rule => rule.is_active)
            .map(rule => (
              <li key={rule.id}>
                <strong>{rule.field}:</strong>{' '}
                {rule.rule_type === 'min_words' && `minimum ${rule.value} words`}
                {rule.rule_type === 'min_count' && `minimum ${rule.value} items`}
                {rule.rule_type === 'exists' && 'must be filled'}
              </li>
            ))}
        </ul>
        {currentRules.filter(r => r.is_active).length === 0 && (
          <p style={{ margin: 0, color: 'var(--admin-text-secondary)', fontSize: '0.85rem' }}>
            No active requirements
          </p>
        )}
      </div>
    </div>
  );
};

export default AdminSettings;
